import {
  loadLocationData,
  loadLocationMeters,
  loadMeterModels,
  loadOwnerValues,
  loadTenantValues,
  loadThirdParties,
} from '@/features/location-bootstrap/location-bootstrap-requests';
import { deriveManufacturers } from '@/features/location-bootstrap/location-bootstrap-response-mappers';
import type { LocationBootstrapPayload } from '@/features/location-bootstrap/location-bootstrap-types';
import {
  createInventoryAxiosClient,
  createLocationApiKeyAxiosClient,
  type InventoryAxiosClient,
} from '@/shared/api/default-axios-client';
import { uniqueStrings } from '@/shared/unique-strings';

interface LocationBootstrapClientInput {
  axiosClient?: InventoryAxiosClient;
}

export class LocationBootstrapClient {
  private readonly axios: InventoryAxiosClient;

  constructor(input: LocationBootstrapClientInput = {}) {
    this.axios = input.axiosClient ?? createInventoryAxiosClient();
  }

  async load(input: {
    locationApiKey: string;
    locationId: number | string;
    refreshCache?: boolean;
  }): Promise<LocationBootstrapPayload> {
    if (!this.axios.defaults.baseURL) {
      throw new Error('API base URL is required for location bootstrap.');
    }

    const axios = createLocationApiKeyAxiosClient({
      axiosClient: this.axios,
      locationApiKey: input.locationApiKey,
      refreshCache: input.refreshCache,
    });
    const loadedAt = new Date().toISOString();

    const [locationData, existingMeters, meterModels, distinctTenants, distinctOwners, thirdParties] =
      await Promise.all([
        loadLocationData({ axios, locationId: input.locationId }),
        loadLocationMeters({
          axios,
          locationId: input.locationId,
        }),
        loadMeterModels(axios),
        loadTenantValues(axios),
        loadOwnerValues(axios),
        loadThirdParties(axios),
      ]);

    return {
      buildings: locationData.buildings,
      existingMeters,
      loadedAt,
      location: locationData.location,
      locationId: input.locationId,
      manufacturers: deriveManufacturers(meterModels),
      meterModels,
      ownerSuggestions: uniqueStrings([
        ...distinctOwners,
        ...existingMeters.flatMap(meter =>
          typeof meter.ownerName === 'string' && meter.ownerName.trim().length > 0 ? [meter.ownerName] : [],
        ),
      ]),
      tenants: uniqueStrings([
        ...distinctTenants,
        ...existingMeters.flatMap(meter =>
          typeof meter.tenant === 'string' && meter.tenant.trim().length > 0 ? [meter.tenant] : [],
        ),
      ]),
      thirdParties,
    };
  }
}
